import { feedbackPoints } from "@/utils/aiFeedback";
import { ref, reactive, computed, shallowRef } from "vue";
import { useAiStoreRefactor } from "@/stores/aiStoreRefactor";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { useDeadlinesStoreRefactor } from "@/stores/deadlinesStoreRefactor";
import { useDeadlineCategoriesStoreRefactor } from "@/stores/deadlineCategoriesStoreRefactor";
import { DRAFT_CHECK_SECTIONS } from "@/utils/aiSkills";
import {
  existingMentions,
  hasSamePlaceholders,
  placeholderTextToDoc,
  placeholderTextToPreview,
} from "@/utils/mentionPlaceholders";

const GENERIC_ERROR = "De AI-check is niet gelukt. Probeer het opnieuw.";

/** Tomorrow as YYYY-MM-DD in local time (the default date of a new task). */
export function tomorrowIso(now = new Date()) {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Who a suggested task is for: null = everyone, otherwise a user id.
 * @param {string} assignee - "self", "everyone" or a mention placeholder like "[[m:1]]"
 * @param {Record<string, object>} mentions - from docToPlaceholderText
 * @param {number} currentUserId
 */
export function resolveAssignee(assignee, mentions, currentUserId) {
  if (assignee === "everyone") return null;
  const mention = mentions?.[/^\[\[m:(\d+)\]\]$/.exec(assignee || "")?.[1]];
  if (mention?.type !== "userMention") return currentUserId;
  return mention.attrs?.id === "*" ? null : mention.attrs?.id;
}

/**
 * One "AI-check" of a report draft: runs every applicable skill in a single request
 * and keeps what the worker does with each suggestion.
 *
 * Nothing is written by this composable except to the draft itself: accepted traits
 * and mentions go into the form, accepted tasks are queued on the draft
 * (`ai_follow_ups`) and created after the report is saved (`createQueuedTasks`).
 * The draft also carries `ai_usage_log_ids` and `ai_accepted` (accepted suggestion
 * keys per usage log) so the backend can record the outcome on save.
 *
 * Suggestions belong to the text that was checked: when the text changes afterwards,
 * `isStale` is true and the rewrite can no longer be applied.
 *
 * @param {object} options
 * @param {import("vue").Ref<object>} options.report - the draft
 * @param {import("vue").Ref<object>} options.editor - AtMentionBox instance
 * @param {import("vue").Ref<number>} options.reportCategoryId
 * @param {import("vue").Ref<boolean>} options.includeTraits - trait picker is shown
 */
export function useAiDraftCheck({
  report,
  editor,
  reportCategoryId,
  includeTraits,
}) {
  const aiStore = useAiStoreRefactor();
  const authStore = useAuthStoreRefactor();

  const open = ref(false);
  const checkedDraft = shallowRef(null); // { text, mentions } the suggestions belong to
  const sections = reactive({}); // skill key -> { status: "loading"|"ok"|"error", data?, message? }
  const reviewApplied = ref(false);
  const reviewRating = ref(null);
  const rejectedKeys = reactive({ mention_link: [], trait_suggest: [] });
  const followUpRows = ref([]);

  const show = () => (open.value = true);
  const close = () => (open.value = false);

  const skillKeys = computed(() =>
    DRAFT_CHECK_SECTIONS.map((section) => section.key).filter(
      (key) => key !== "trait_suggest" || includeTraits.value,
    ),
  );

  const currentDraft = () =>
    editor.value?.getAiDraft() || { text: "", mentions: {} };

  // The editor is not reactive; the draft's description changes whenever it does.
  const currentText = computed(() => {
    void report.value.description;
    return currentDraft().text;
  });
  const hasText = computed(() => !!currentText.value.trim());
  const checking = computed(() => aiStore.checking);
  const hasResults = computed(() => !!checkedDraft.value);
  const isStale = computed(
    () => !!checkedDraft.value && currentText.value !== checkedDraft.value.text,
  );

  const data = (key) =>
    sections[key]?.status === "ok" ? sections[key].data : null;

  const acceptedKeys = (key) => {
    const logId = data(key)?.usage_log_id;
    return (logId && report.value.ai_accepted?.[logId]) || [];
  };

  const markAccepted = (key, suggestionKey) => {
    const logId = data(key)?.usage_log_id;
    if (!logId) return;
    const accepted = report.value.ai_accepted || {};
    report.value.ai_accepted = {
      ...accepted,
      [logId]: [
        ...new Set([...(accepted[logId] || []), String(suggestionKey)]),
      ],
    };
  };

  const trackUsage = (usageLogId) => {
    report.value.ai_usage_log_ids = [
      ...new Set([...(report.value.ai_usage_log_ids || []), usageLogId]),
    ];
  };

  const buildFollowUpRows = (followUps, usageLogId, mentions) => {
    const categories = useDeadlineCategoriesStoreRefactor().categories || [];
    return followUps.map((followUp, index) => ({
      id: `${usageLogId}-${index}`,
      description: placeholderTextToPreview(followUp.description, mentions),
      deadline: followUp.due_date || tomorrowIso(),
      deadline_category_id:
        followUp.deadline_category_id ||
        (categories.length === 1 ? categories[0].id : null),
      user_id: resolveAssignee(followUp.assignee, mentions, authStore.user?.id),
      ai_usage_log_id: usageLogId,
      state: "open", // open | added | rejected
    }));
  };

  const storeSection = (key, section, draft) => {
    if (!section) {
      sections[key] = { status: "error", message: GENERIC_ERROR };
    } else if (section.error) {
      sections[key] = { status: "error", message: section.error.message };
    } else {
      sections[key] = { status: "ok", data: section };
      trackUsage(section.usage_log_id);
      if (key === "follow_up_suggest") {
        followUpRows.value = buildFollowUpRows(
          section.follow_ups,
          section.usage_log_id,
          draft.mentions,
        );
      }
    }
  };

  /** Runs the check for all skills, or only `keys` (retry of failed sections). */
  const run = async (keys = skillKeys.value) => {
    const draft = currentDraft();
    if (!draft.text.trim()) return;

    const fullRun = keys.length === skillKeys.value.length;
    open.value = true;
    keys.forEach((key) => (sections[key] = { status: "loading" }));
    if (fullRun) {
      Object.keys(sections).forEach((key) => {
        if (!keys.includes(key)) delete sections[key];
      });
      checkedDraft.value = draft;
      reviewApplied.value = false;
      reviewRating.value = null;
      rejectedKeys.mention_link = [];
      rejectedKeys.trait_suggest = [];
      followUpRows.value = [];
    }

    try {
      const result = await aiStore.draftCheck({
        text: draft.text,
        skills: keys,
        reportCategoryId: reportCategoryId.value,
        selectedTraitIds: report.value.trait_ids || [],
        existingMentions: existingMentions(draft.mentions),
      });
      keys.forEach((key) => storeSection(key, result?.[key], draft));
    } catch (error) {
      // The API client already showed the error; keep the sheet usable.
      const message = error?.response?.data?.message || GENERIC_ERROR;
      keys.forEach((key) => (sections[key] = { status: "error", message }));
    }
  };

  /** Retries one failed section; a changed text needs a full new check. */
  const retry = (key) => (isStale.value ? run() : run([key]));

  // --- Tekst ---------------------------------------------------------------
  const review = computed(() => {
    const result = data("report_review");
    if (!result) return null;
    const mentions = checkedDraft.value?.mentions;
    return {
      ...result,
      // [{ text, severity }], most serious first, with mentions shown by name
      feedback_bullets: feedbackPoints(result.feedback_bullets).map(
        (point) => ({
          ...point,
          text: placeholderTextToPreview(point.text, mentions),
          ...(point.quote
            ? { quote: placeholderTextToPreview(point.quote, mentions) }
            : {}),
        }),
      ),
      preview: placeholderTextToPreview(result.suggested_rewrite, mentions),
      // The text that was checked, in the same readable form, to mark the changes
      original_preview: placeholderTextToPreview(
        checkedDraft.value?.text || "",
        mentions,
      ),
    };
  });

  // Only when server and client agree every mention survives, and the text is
  // still the one that was checked.
  const canApplyRewrite = computed(() => {
    const result = data("report_review");
    if (!result?.rewrite_applicable || isStale.value || reviewApplied.value)
      return false;
    return (
      hasSamePlaceholders(checkedDraft.value.text, result.suggested_rewrite) &&
      placeholderTextToDoc(
        result.suggested_rewrite,
        checkedDraft.value.mentions,
      ) !== null
    );
  });

  const applyRewrite = () => {
    if (!canApplyRewrite.value) return;
    const result = data("report_review");
    editor.value.applyAiRewrite(
      placeholderTextToDoc(
        result.suggested_rewrite,
        checkedDraft.value.mentions,
      ),
    );
    reviewApplied.value = true;
    // The applied text is now the text the remaining suggestions are shown against.
    checkedDraft.value = currentDraft();
    aiStore.feedback({ usageLogId: result.usage_log_id, outcome: "applied" });
  };

  const rateReview = (rating) => {
    const result = data("report_review");
    if (!result) return;
    reviewRating.value = rating;
    aiStore.feedback({ usageLogId: result.usage_log_id, rating });
  };

  // --- Vermeldingen ----------------------------------------------------------
  const mentionLinks = computed(() =>
    (data("mention_link")?.links || [])
      .map((link) => ({ ...link, key: `${link.type}:${link.id}` }))
      .filter((link) => !rejectedKeys.mention_link.includes(link.key))
      .map((link) => ({
        ...link,
        linked: acceptedKeys("mention_link").includes(link.key),
        // The phrase must still be in the text to be replaced.
        available: currentText.value.includes(link.phrase),
      })),
  );

  const acceptMentionLink = (link) => {
    if (editor.value?.replaceTextWithMention(link.phrase, link)) {
      markAccepted("mention_link", link.key);
    }
  };

  const rejectMentionLink = (link) => rejectedKeys.mention_link.push(link.key);

  // --- Kenmerken ---------------------------------------------------------------
  const traitSuggestions = computed(() =>
    (data("trait_suggest")?.suggestions || []).filter(
      (suggestion) => !rejectedKeys.trait_suggest.includes(suggestion.trait_id),
    ),
  );

  const acceptTrait = (traitId) => {
    const current = report.value.trait_ids || [];
    if (!current.includes(traitId))
      report.value.trait_ids = [...current, traitId];
    markAccepted("trait_suggest", traitId);
  };

  const rejectTrait = (traitId) => rejectedKeys.trait_suggest.push(traitId);

  // --- Taken ---------------------------------------------------------------
  const queuedTasks = computed(() => report.value.ai_follow_ups || []);

  const updateFollowUp = (id, patch) => {
    followUpRows.value = followUpRows.value.map((row) =>
      row.id === id ? { ...row, ...patch } : row,
    );
  };

  /** The worker agrees to a suggested task: it is queued, not yet created. */
  const addFollowUp = (id) => {
    const row = followUpRows.value.find((candidate) => candidate.id === id);
    if (
      !row ||
      !row.description?.trim() ||
      !row.deadline ||
      !row.deadline_category_id
    )
      return;
    report.value.ai_follow_ups = [
      ...queuedTasks.value,
      {
        id: row.id,
        description: row.description.trim(),
        deadline: row.deadline,
        deadline_category_id: row.deadline_category_id,
        user_id: row.user_id,
        ai_usage_log_id: row.ai_usage_log_id,
      },
    ];
    updateFollowUp(id, { state: "added" });
  };

  const rejectFollowUp = (id) => updateFollowUp(id, { state: "rejected" });

  const removeQueuedTask = (id) => {
    report.value.ai_follow_ups = queuedTasks.value.filter(
      (task) => task.id !== id,
    );
    updateFollowUp(id, { state: "open" });
  };

  /**
   * Creates the queued tasks through the normal task flow. Call after the report
   * has been saved; a failure is shown by the API client and does not stop the rest.
   * @returns {Promise<number>} number of tasks created
   */
  const createQueuedTasks = async (tasks, accountId) => {
    const deadlinesStore = useDeadlinesStoreRefactor();
    let created = 0;
    for (const task of tasks || []) {
      try {
        await deadlinesStore.create({
          account_id: accountId,
          description: task.description,
          deadline: task.deadline,
          deadline_category_id: task.deadline_category_id,
          user_id: task.user_id,
          ai_usage_log_id: task.ai_usage_log_id,
        });
        created += 1;
      } catch (error) {
        console.warn(
          "Suggested task could not be created",
          error?.response?.status,
        );
      }
    }
    return created;
  };

  // --- Overview ----------------------------------------------------------------
  const openCount = (key) => {
    switch (key) {
      case "report_review":
        return review.value && !reviewApplied.value
          ? review.value.feedback_bullets.length
          : 0;
      case "mention_link":
        return mentionLinks.value.filter(
          (link) => !link.linked && link.available,
        ).length;
      case "follow_up_suggest":
        return followUpRows.value.filter((row) => row.state === "open").length;
      case "trait_suggest":
        return traitSuggestions.value.filter(
          (suggestion) =>
            !(report.value.trait_ids || []).includes(suggestion.trait_id),
        ).length;
      default:
        return 0;
    }
  };

  // How many suggestions a section had in total (handled or not).
  const totalCount = (key) => {
    switch (key) {
      case "report_review":
        return review.value ? 1 : 0;
      case "mention_link":
        return (data("mention_link")?.links || []).length;
      case "follow_up_suggest":
        return followUpRows.value.length;
      case "trait_suggest":
        return (data("trait_suggest")?.suggestions || []).length;
      default:
        return 0;
    }
  };

  /** One entry per section in display order, for the sheet and the summary line. */
  const sectionList = computed(() =>
    DRAFT_CHECK_SECTIONS.filter((section) => sections[section.key]).map(
      (section) => ({
        ...section,
        status: sections[section.key].status,
        message: sections[section.key].message,
        count: openCount(section.key),
        total: totalCount(section.key),
      }),
    ),
  );

  return {
    open,
    show,
    close,
    checking,
    hasText,
    hasResults,
    isStale,
    sectionList,
    run,
    retry,
    review,
    reviewApplied,
    reviewRating,
    canApplyRewrite,
    applyRewrite,
    rateReview,
    mentionLinks,
    acceptMentionLink,
    rejectMentionLink,
    traitSuggestions,
    acceptTrait,
    rejectTrait,
    followUpRows,
    updateFollowUp,
    addFollowUp,
    rejectFollowUp,
    queuedTasks,
    removeQueuedTask,
    createQueuedTasks,
  };
}
